import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  Building2Icon,
  CheckCircle2Icon,
  FlagIcon,
  MailIcon,
  MessageSquareTextIcon,
  ShieldCheckIcon,
  SparklesIcon,
  UserIcon,
  UsersIcon,
} from "lucide-react";
import { supabase } from "../lib/supabaseClient";

const LOGO_URL =
  "https://yswafknmqpwjrsxjslbu.supabase.co/storage/v1/object/public/logos//logosmall.png";

const getFunctionErrorMessage = async (error, fallback) => {
  if (!error) return fallback;

  try {
    if (error.context?.json) {
      const payload = await error.context.json();
      return payload?.error || payload?.message || fallback;
    }
  } catch (parseError) {
    console.warn("Kunde inte läsa Edge Function-felet:", parseError);
  }

  return error.message || fallback;
};

export default function AccessRequest() {
  const [searchParams] = useSearchParams();
  const initialAccountType =
    searchParams.get("typ") === "club" ? "club" : "private";

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [accountType, setAccountType] = useState(initialAccountType);
  const [organization, setOrganization] = useState("");
  const [usageFrequency, setUsageFrequency] = useState("");
  const [motivation, setMotivation] = useState("");
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [website, setWebsite] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);

  const motivationLength = motivation.trim().length;
  const canSubmit = useMemo(
    () =>
      fullName.trim().length >= 2 &&
      email.trim() &&
      (accountType !== "club" || organization.trim().length >= 2) &&
      usageFrequency &&
      motivationLength >= 40 &&
      acceptedPrivacy &&
      !isSubmitting,
    [
      fullName,
      email,
      accountType,
      organization,
      usageFrequency,
      motivationLength,
      acceptedPrivacy,
      isSubmitting,
    ]
  );

  const submitHint = useMemo(() => {
    if (fullName.trim().length < 2) return "Fyll i ditt namn för att gå vidare.";
    if (!email.trim()) return "Fyll i din e-postadress för att gå vidare.";
    if (accountType === "club" && organization.trim().length < 2) {
      return "Fyll i golfklubbens eller organisationens namn.";
    }
    if (!usageFrequency) return "Välj hur du planerar att använda tjänsten.";
    if (motivationLength < 40) {
      return `Skriv minst ${40 - motivationLength} tecken till i motiveringen.`;
    }
    if (!acceptedPrivacy) {
      return "Godkänn att uppgifterna används för att hantera ansökan.";
    }
    return "";
  }, [
    fullName,
    email,
    accountType,
    organization,
    usageFrequency,
    motivationLength,
    acceptedPrivacy,
  ]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!fullName.trim() || !email.trim()) {
      setErrorMessage("Fyll i namn och e-postadress.");
      return;
    }

    if (accountType === "club" && organization.trim().length < 2) {
      setErrorMessage("Fyll i golfklubbens eller organisationens namn.");
      return;
    }

    if (!usageFrequency) {
      setErrorMessage("Välj hur du planerar att använda HoleInOne.");
      return;
    }

    if (motivationLength < 40) {
      setErrorMessage(
        "Berätta lite mer om hur du vill använda tjänsten. Minst 40 tecken behövs."
      );
      return;
    }

    if (!acceptedPrivacy) {
      setErrorMessage(
        "Du behöver godkänna att uppgifterna används för att hantera ansökan."
      );
      return;
    }

    setIsSubmitting(true);
    setErrorMessage("");

    try {
      const { data, error } = await supabase.functions.invoke(
        "submit-beta-request",
        {
          body: {
            fullName: fullName.trim(),
            email: email.trim().toLowerCase(),
            accountType,
            organization: organization.trim(),
            usageFrequency,
            motivation: motivation.trim(),
            website,
          },
        }
      );

      if (error) {
        const message = await getFunctionErrorMessage(
          error,
          "Ansökan kunde inte skickas. Försök igen om en stund."
        );
        setErrorMessage(message);
        return;
      }

      if (!data?.success) {
        setErrorMessage(
          data?.error ||
            "Ansökan kunde inte skickas. Försök igen om en stund."
        );
        return;
      }

      setIsComplete(true);
    } catch (error) {
      console.error("Betaansökan misslyckades:", error);
      setErrorMessage(
        "Ansökan kunde inte skickas. Kontrollera anslutningen och försök igen."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f3f0e7] text-[#1f2d26]">
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -right-32 -top-28 h-96 w-96 rounded-full bg-[#dfe9e1] blur-3xl" />
        <div className="absolute -bottom-40 -left-32 h-96 w-96 rounded-full bg-[#eee2c8] blur-3xl" />
      </div>

      <main className="relative mx-auto grid min-h-screen w-full max-w-6xl gap-6 px-4 py-6 sm:px-6 sm:py-10 lg:grid-cols-[0.86fr_1.14fr] lg:items-stretch">
        <section className="relative overflow-hidden rounded-[28px] bg-[#183d2d] p-6 text-white shadow-[0_22px_65px_rgba(31,45,38,0.16)] sm:p-8 lg:p-10">
          <div className="pointer-events-none absolute -right-28 -top-20 h-72 w-72 rounded-full bg-[#b08b45]/22 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-44 -left-20 h-80 w-80 rounded-full bg-white/[0.08] blur-3xl" />

          <div className="relative flex h-full flex-col">
            <Link
              to="/"
              className="inline-flex w-fit items-center gap-2 text-xs font-semibold text-white/58 transition hover:text-white"
            >
              <ArrowLeftIcon className="h-4 w-4" />
              Till startsidan
            </Link>

            <div className="mt-10 flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-white/15 bg-white/10 shadow-sm">
                <img
                  src={LOGO_URL}
                  alt="HoleInOne"
                  className="h-8 w-8 object-contain brightness-125"
                />
              </div>

              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-white/50">
                  HoleInOne.se
                </p>
                <p className="mt-0.5 text-sm font-medium text-white/80">
                  Privat beta
                </p>
              </div>
            </div>

            <div className="mt-10">
              <span className="inline-flex items-center gap-2 rounded-full border border-[#d8c38d]/20 bg-[#d8c38d]/10 px-3 py-1.5 text-[9px] font-semibold uppercase tracking-[0.18em] text-[#e0cd98]">
                <SparklesIcon className="h-3.5 w-3.5" />
                Ansökan om åtkomst
              </span>

              <h1 className="mt-5 text-3xl font-semibold tracking-tight sm:text-4xl">
                Hjälp oss forma nästa version av HoleInOne.
              </h1>
              <p className="mt-4 text-sm leading-7 text-white/60 sm:text-base">
                Under betan prioriterar vi användare som vill testa tjänsten i
                riktiga golftävlingar och ge konkret återkoppling på flöden,
                funktioner och resultatvisning.
              </p>
            </div>

            <div className="mt-8 space-y-3">
              {[
                {
                  icon: MessageSquareTextIcon,
                  title: "Berätta hur tjänsten ska användas",
                  text: "Beskriv ert upplägg, antal spelare och vad ni vill lösa.",
                },
                {
                  icon: ShieldCheckIcon,
                  title: "Ansökan granskas manuellt",
                  text: "Vi släpper in användare stegvis under betaperioden.",
                },
                {
                  icon: MailIcon,
                  title: "Godkända ansökningar får en kod",
                  text: "Koden används en gång när kontot skapas.",
                },
              ].map((item) => {
                const Icon = item.icon;

                return (
                  <div
                    key={item.title}
                    className="flex gap-3 rounded-2xl border border-white/10 bg-white/[0.055] p-4"
                  >
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/10 text-[#dcc78f]">
                      <Icon className="h-4 w-4" />
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-white">
                        {item.title}
                      </p>
                      <p className="mt-1 text-xs leading-5 text-white/48">
                        {item.text}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-auto pt-10 text-[10px] leading-5 text-white/35">
              Betan är kostnadsfri under testperioden. Åtkomst innebär inte en
              garanti om framtida funktioner eller permanent kostnadsfri
              användning.
            </div>
          </div>
        </section>

        <section className="overflow-hidden rounded-[28px] border border-[#d8d2c4] bg-[#fffdf8] shadow-[0_22px_65px_rgba(31,45,38,0.1)]">
          {isComplete ? (
            <div className="flex min-h-full flex-col items-center justify-center px-6 py-14 text-center sm:px-10">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[#e6f0e9] text-[#287148]">
                <CheckCircle2Icon className="h-8 w-8" />
              </span>

              <p className="mt-6 text-[10px] font-semibold uppercase tracking-[0.22em] text-[#9a7735]">
                Ansökan mottagen
              </p>
              <h2 className="mt-3 text-2xl font-semibold text-[#183d2d] sm:text-3xl">
                Tack, {fullName.split(" ")[0]}.
              </h2>
              <p className="mt-4 max-w-md text-sm leading-7 text-[#68716c]">
                Din ansökan är registrerad. Om den godkänns får du en personlig
                åtkomstkod till <strong>{email}</strong> som används när kontot
                skapas.
              </p>

              <div className="mt-8 flex w-full max-w-sm flex-col gap-2 sm:flex-row">
                <Link
                  to="/"
                  className="inline-flex flex-1 items-center justify-center rounded-xl border border-[#cfc7b8] bg-white px-4 py-3 text-sm font-semibold text-[#183d2d] transition hover:bg-[#f5f1e8]"
                >
                  Till startsidan
                </Link>
                <Link
                  to="/login"
                  className="inline-flex flex-1 items-center justify-center rounded-xl bg-[#183d2d] px-4 py-3 text-sm font-semibold text-white transition hover:bg-[#214d39]"
                >
                  Logga in
                </Link>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="px-5 py-6 sm:px-8 sm:py-8">
              <div className="border-b border-[#e4ded1] pb-6">
                <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#9a7735]">
                  Betaansökan
                </p>
                <h2 className="mt-2 text-2xl font-semibold text-[#183d2d]">
                  Berätta om ditt användningsområde
                </h2>
                <p className="mt-2 text-sm leading-6 text-[#68716c]">
                  Formuläret tar ungefär två minuter att fylla i.
                </p>
                <p className="mt-2 text-[11px] text-[#8a8f8b]">
                  <span className="font-bold text-[#9a7735]">*</span> Obligatoriskt fält
                </p>
              </div>

              <div className="mt-6 space-y-5">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label
                      htmlFor="request-name"
                      className="mb-1.5 block text-[10px] font-semibold uppercase tracking-[0.15em] text-[#68716c]"
                    >
                      Namn{" "}
                      <span aria-hidden="true" className="font-bold text-[#9a7735]">
                        *
                      </span>
                    </label>
                    <div className="relative">
                      <UserIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#8d948f]" />
                      <input
                        id="request-name"
                        type="text"
                        required
                        autoComplete="name"
                        value={fullName}
                        onChange={(event) => setFullName(event.target.value)}
                        placeholder="För- och efternamn"
                        className="w-full rounded-xl border border-[#c9c1b2] bg-white py-3 pl-10 pr-3 text-sm outline-none placeholder:text-[#a0a59f] focus:border-[#183d2d] focus:ring-2 focus:ring-[#183d2d]/10"
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="request-email"
                      className="mb-1.5 block text-[10px] font-semibold uppercase tracking-[0.15em] text-[#68716c]"
                    >
                      E-postadress{" "}
                      <span aria-hidden="true" className="font-bold text-[#9a7735]">
                        *
                      </span>
                    </label>
                    <div className="relative">
                      <MailIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#8d948f]" />
                      <input
                        id="request-email"
                        type="email"
                        required
                        autoComplete="email"
                        value={email}
                        onChange={(event) => setEmail(event.target.value)}
                        placeholder="namn@exempel.se"
                        className="w-full rounded-xl border border-[#c9c1b2] bg-white py-3 pl-10 pr-3 text-sm outline-none placeholder:text-[#a0a59f] focus:border-[#183d2d] focus:ring-2 focus:ring-[#183d2d]/10"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <span className="mb-2 block text-[10px] font-semibold uppercase tracking-[0.15em] text-[#68716c]">
                    Jag ansöker som{" "}
                    <span aria-hidden="true" className="font-bold text-[#9a7735]">
                      *
                    </span>
                  </span>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <button
                      type="button"
                      onClick={() => setAccountType("private")}
                      className={`flex items-start gap-3 rounded-xl border p-4 text-left transition ${
                        accountType === "private"
                          ? "border-[#183d2d] bg-[#e8f0ea] shadow-sm"
                          : "border-[#d8d2c4] bg-white hover:border-[#b9b09e]"
                      }`}
                    >
                      <UsersIcon className="mt-0.5 h-5 w-5 shrink-0 text-[#183d2d]" />
                      <span>
                        <span className="block text-sm font-semibold text-[#183d2d]">
                          Privatperson / golfgäng
                        </span>
                        <span className="mt-1 block text-xs leading-5 text-[#68716c]">
                          För privata tävlingar och återkommande kompisgäng.
                        </span>
                      </span>
                    </button>

                    <button
                      type="button"
                      onClick={() => setAccountType("club")}
                      className={`flex items-start gap-3 rounded-xl border p-4 text-left transition ${
                        accountType === "club"
                          ? "border-[#183d2d] bg-[#e8f0ea] shadow-sm"
                          : "border-[#d8d2c4] bg-white hover:border-[#b9b09e]"
                      }`}
                    >
                      <Building2Icon className="mt-0.5 h-5 w-5 shrink-0 text-[#183d2d]" />
                      <span>
                        <span className="flex items-center gap-2 text-sm font-semibold text-[#183d2d]">
                          Golfklubb
                          <span className="rounded-full bg-[#efe7d5] px-2 py-0.5 text-[8px] uppercase tracking-[0.12em] text-[#9a7735]">
                            Intresse
                          </span>
                        </span>
                        <span className="mt-1 block text-xs leading-5 text-[#68716c]">
                          För klubbverksamhet och framtida klubbportal.
                        </span>
                      </span>
                    </button>
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="request-organization"
                    className="mb-1.5 block text-[10px] font-semibold uppercase tracking-[0.15em] text-[#68716c]"
                  >
                    {accountType === "club"
                      ? "Golfklubb / organisation"
                      : "Hemmaklubb eller gruppnamn"}{" "}
                    {accountType === "club" ? (
                      <span
                        aria-hidden="true"
                        className="font-bold text-[#9a7735]"
                      >
                        *
                      </span>
                    ) : (
                      <span className="normal-case tracking-normal text-[#a0a59f]">
                        (valfritt)
                      </span>
                    )}
                  </label>
                  <div className="relative">
                    <FlagIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#8d948f]" />
                    <input
                      id="request-organization"
                      type="text"
                      required={accountType === "club"}
                      aria-required={accountType === "club"}
                      value={organization}
                      onChange={(event) => setOrganization(event.target.value)}
                      placeholder={
                        accountType === "club"
                          ? "Klubbens namn"
                          : "Klubbens namn"
                      }
                      className="w-full rounded-xl border border-[#c9c1b2] bg-white py-3 pl-10 pr-3 text-sm outline-none placeholder:text-[#a0a59f] focus:border-[#183d2d] focus:ring-2 focus:ring-[#183d2d]/10"
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="request-frequency"
                    className="mb-1.5 block text-[10px] font-semibold uppercase tracking-[0.15em] text-[#68716c]"
                  >
                    Planerad användning{" "}
                    <span aria-hidden="true" className="font-bold text-[#9a7735]">
                      *
                    </span>
                  </label>
                  <select
                    id="request-frequency"
                    required
                    value={usageFrequency}
                    onChange={(event) => setUsageFrequency(event.target.value)}
                    className="w-full rounded-xl border border-[#c9c1b2] bg-white px-3 py-3 text-sm outline-none focus:border-[#183d2d] focus:ring-2 focus:ring-[#183d2d]/10"
                  >
                    <option value="">Välj ett alternativ</option>
                    <option value="single_event">En kommande tävling</option>
                    <option value="few_per_year">Några tävlingar per år</option>
                    <option value="monthly">Ungefär varje månad</option>
                    <option value="club_operations">Löpande klubbverksamhet</option>
                    <option value="other">Annat upplägg</option>
                  </select>
                </div>

                <div>
                  <div className="mb-1.5 flex items-end justify-between gap-3">
                    <label
                      htmlFor="request-motivation"
                      className="text-[10px] font-semibold uppercase tracking-[0.15em] text-[#68716c]"
                    >
                      Hur vill du använda HoleInOne?{" "}
                      <span aria-hidden="true" className="font-bold text-[#9a7735]">
                        *
                      </span>
                    </label>
                    <span
                      className={`text-[10px] tabular-nums ${
                        motivationLength >= 40
                          ? "text-[#287148]"
                          : "text-[#9a9185]"
                      }`}
                    >
                      {motivationLength}/40 min
                    </span>
                  </div>
                  <textarea
                    id="request-motivation"
                    rows={6}
                    required
                    value={motivation}
                    onChange={(event) => setMotivation(event.target.value)}
                    placeholder="Beskriv tävlingen, ungefär hur många ni är och vilka delar av tjänsten som är mest intressanta för er."
                    className="w-full resize-y rounded-xl border border-[#c9c1b2] bg-white px-3 py-3 text-sm leading-6 outline-none placeholder:text-[#a0a59f] focus:border-[#183d2d] focus:ring-2 focus:ring-[#183d2d]/10"
                  />
                </div>

                <div className="hidden" aria-hidden="true">
                  <label htmlFor="request-website">Webbplats</label>
                  <input
                    id="request-website"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={website}
                    onChange={(event) => setWebsite(event.target.value)}
                  />
                </div>

                <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-[#e0d9cc] bg-[#f8f5ed] p-3.5">
                  <input
                    type="checkbox"
                    required
                    checked={acceptedPrivacy}
                    onChange={(event) =>
                      setAcceptedPrivacy(event.target.checked)
                    }
                    className="mt-0.5 h-4 w-4 accent-[#183d2d]"
                  />
                  <span className="text-xs leading-5 text-[#68716c]">
                    Jag godkänner att uppgifterna sparas och används för att
                    bedöma och administrera min betaansökan.{" "}
                    <span aria-hidden="true" className="font-bold text-[#9a7735]">
                      *
                    </span>
                  </span>
                </label>

                {errorMessage && (
                  <div
                    role="alert"
                    className="flex items-start gap-3 rounded-xl border border-[#dfc5c0] bg-[#fff8f6] px-3.5 py-3 text-sm text-[#a0443e]"
                  >
                    <ShieldCheckIcon className="mt-0.5 h-4 w-4 shrink-0" />
                    <p className="leading-5">{errorMessage}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={!canSubmit}
                  className="group inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#183d2d] px-5 py-3.5 text-sm font-semibold text-white shadow-[0_8px_20px_rgba(24,61,45,0.17)] transition hover:bg-[#214d39] disabled:cursor-not-allowed disabled:opacity-45"
                >
                  {isSubmitting ? "Skickar ansökan…" : "Skicka ansökan"}
                  {!isSubmitting && (
                    <ArrowRightIcon className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                  )}
                </button>

                {!canSubmit && !isSubmitting && submitHint && (
                  <p className="-mt-2 text-center text-[11px] leading-5 text-[#8a8f8b]">
                    {submitHint}
                  </p>
                )}

                <p className="text-center text-xs text-[#7b817d]">
                  Har du redan en kod?{" "}
                  <Link
                    to="/register"
                    className="font-semibold text-[#183d2d] hover:text-[#9a7735] hover:underline"
                  >
                    Skapa konto
                  </Link>
                </p>
              </div>
            </form>
          )}
        </section>
      </main>
    </div>
  );
}